import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Grid3x3,
  List,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Star,
  Calendar,
  Users,
  Building2,
  User,
  TrendingUp,
  Settings2,
  Save,
  Loader2,
  Filter,
  X,
  Shield,
  ExternalLink,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PaginationControls } from '@/components/pagination-controls';
import { usePagination } from '@/hooks/use-pagination';
import { paginateArray } from '@/types/pagination';
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import type { Employee as ApiEmployee, ResourceAllocation } from '@/types';
import { fetchEmployees, fetchResourceAllocations, fetchSkills, type SkillSummaryResponse } from '@/lib/data-service';
import type { EmployeeCompanyAccessRow } from '@/types/integrations-redesign';
import {
  getEmployeeCompanyAccess,
  updateEmployeeCompanyAccess,
} from '@/services/integrationsRedesignService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MOCK_ROLES } from '@/lib/roles-mock-data';
import { toast } from 'sonner';
import {
  DirectoryFilterSheet,
  countActiveDirectoryFilters,
  hasActiveDirectoryFilters,
  EMPTY_DIRECTORY_FILTERS,
  type DirectoryFilters,
} from '@/components/directory-filter-sheet';

interface Skill {
  name: string;
  proficiency: number; // 1-5
  category: string;
}

interface ProjectAllocation {
  projectId: number;
  projectName: string;
  projectCode: string;
  allocation: number; // percentage
  startDate: string;
  endDate: string;
  role: string;
}

interface WorkingPattern {
  name: string;
  fte: number;
  hoursPerDay: number;
  totalHoursPerWeek: number;
  workingDays: string[];
  /** Per-day hours keyed by full day name (e.g. { Monday: 7.5, Tuesday: 7.5 }). When present, takes precedence over the uniform hoursPerDay value. */
  dailyHours?: Record<string, number>;
}

interface Rates {
  billRate: {
    day: number;
    hour: number;
  };
  costRate: {
    day: number;
    hour: number;
  };
}

interface DirectoryResource {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  location: string;
  image: string;
  type: 'Person' | 'Asset';
  status: 'Active' | 'On Leave' | 'Inactive';
  grade: string;
  resourceType: string;
  skills: Skill[];
  workingPattern: WorkingPattern;
  projectAllocations: ProjectAllocation[];
  rates: Rates;
}

const DEFAULT_WORKING_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const DEFAULT_HOURS_PER_DAY = 8;
const DEFAULT_HOURS_PER_WEEK = 40;

/** Map day-number strings to full day names.
 *  Supports both ISO 8601 (Mon=1 … Sun=7) and .NET/JS DayOfWeek (Sun=0 … Sat=6). */
const DAY_NUMBER_MAP: Record<string, string> = {
  '0': 'Sunday',
  '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', '4': 'Thursday',
  '5': 'Friday', '6': 'Saturday', '7': 'Sunday',
};

/** Map short and full day-name strings to canonical full names. */
const DAY_NAME_MAP: Record<string, string> = {
  Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday',
  Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday',
  Monday: 'Monday', Tuesday: 'Tuesday', Wednesday: 'Wednesday',
  Thursday: 'Thursday', Friday: 'Friday', Saturday: 'Saturday', Sunday: 'Sunday',
};

function parseWorkingDays(workingDays?: string): string[] {
  if (!workingDays) return DEFAULT_WORKING_DAYS;

  const trimmed = workingDays.trim();

  // Handle JSON array format from DB, e.g. "[1,2,3,4,5]" (day numbers)
  if (trimmed.startsWith('[')) {
    try {
      const parsed: (number | string)[] = JSON.parse(trimmed);
      return parsed
        .map(d => DAY_NUMBER_MAP[String(d)] ?? DAY_NAME_MAP[String(d)] ?? String(d))
        .filter(Boolean);
    } catch {
      // Fall through to comma-separated parsing
    }
  }

  // Comma-separated day names or numbers (e.g. "Mon,Tue,Wed" or "1,2,3,4,5")
  return trimmed.split(',').map(d => {
    const t = d.trim();
    return DAY_NUMBER_MAP[t] ?? DAY_NAME_MAP[t] ?? t;
  }).filter(Boolean);
}

/**
 * Parse per-day custom hours from the DB.
 * The DB stores this as a JSON object keyed by day number, e.g. '{"1":7.5,"2":7.5,...}'.
 * Returns a map keyed by full day name, e.g. { Monday: 7.5, Tuesday: 7.5 }.
 */
function parseDailyHours(hoursPerDay?: string): Record<string, number> | undefined {
  if (!hoursPerDay) return undefined;

  const trimmed = hoursPerDay.trim();
  if (!trimmed.startsWith('{')) return undefined;

  try {
    const parsed: Record<string, number> = JSON.parse(trimmed);
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      const dayName = DAY_NUMBER_MAP[key] ?? DAY_NAME_MAP[key];
      if (dayName && typeof value === 'number') {
        result[dayName] = value;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  } catch {
    return undefined;
  }
}

function buildWorkingPattern(emp: ApiEmployee): WorkingPattern {
  const totalHoursPerWeek = emp.hoursPerWeek ?? DEFAULT_HOURS_PER_WEEK;
  const workingDays = parseWorkingDays(emp.workingDays);
  const dailyHours = parseDailyHours(emp.hoursPerDay);

  // The RM webhook may omit weekend days from the WorkingDays field while
  // still including them (with hours > 0) in the HoursPerDay JSON.
  // Reconcile by adding any day present in dailyHours to the working days list.
  if (dailyHours) {
    for (const day of Object.keys(dailyHours)) {
      if (dailyHours[day] > 0 && !workingDays.includes(day)) {
        workingDays.push(day);
      }
    }
  }

  const hoursPerDay = workingDays.length > 0
    ? totalHoursPerWeek / workingDays.length
    : DEFAULT_HOURS_PER_DAY;

  return {
    name: emp.workingPatternName || 'Default',
    fte: totalHoursPerWeek / DEFAULT_HOURS_PER_WEEK,
    hoursPerDay,
    totalHoursPerWeek,
    workingDays,
    dailyHours,
  };
}

/**
 * Build a lookup of employeeId → enriched skills from the Skills API summary.
 * Each skill entry includes the skill name, proficiency rating, and category.
 */
function buildEmployeeSkillsMap(
  skillsSummary: SkillSummaryResponse[]
): Map<string, Skill[]> {
  const map = new Map<string, Skill[]>();
  for (const skill of skillsSummary) {
    for (const resource of skill.resources) {
      const key = String(resource.personId);
      const existing = map.get(key) || [];
      existing.push({
        name: skill.skillName,
        proficiency: resource.proficiency,
        category: skill.category,
      });
      map.set(key, existing);
    }
  }
  return map;
}

function mapApiEmployee(
  emp: ApiEmployee,
  allocations: ResourceAllocation[],
  skillsMap?: Map<string, Skill[]>
): DirectoryResource {
  const empAllocations = allocations.filter(a => a.employeeId === emp.id);
  const enrichedSkills = skillsMap?.get(String(emp.id));
  return {
    id: String(emp.id),
    name: emp.name,
    email: emp.email,
    phone: '',
    role: emp.role,
    department: emp.department,
    location: '',
    image: emp.photo || '',
    type: 'Person',
    status: emp.isAvailable ? 'Active' : 'Inactive',
    grade: '',
    resourceType: '',
    skills: enrichedSkills ?? (emp.skills || []).map(s => ({ name: s, proficiency: 0, category: '' })),
    workingPattern: buildWorkingPattern(emp),
    projectAllocations: empAllocations.map(a => ({
      projectId: a.projectId,
      projectName: a.projectName || `Project ${a.projectId}`,
      projectCode: '',
      allocation: a.allocationPercent ?? a.utilisationPercent ?? 0,
      startDate: a.startDate,
      endDate: a.endDate,
      role: a.role || a.roleName || '',
    })),
    rates: {
      billRate: { day: emp.billRate * 8, hour: emp.billRate },
      costRate: { day: emp.costRate * 8, hour: emp.costRate },
    },
  };
}

// Star rating component
const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating
              ? 'fill-warning-400 text-warning-400'
              : 'fill-neutral-200 text-neutral-200'
          }`}
        />
      ))}
    </div>
  );
};

// Resource detail sheet component
const ResourceDetailSheet: React.FC<{
  employee: DirectoryResource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  navigate: (path: string) => void;
}> = ({ employee, open, onOpenChange, navigate }) => {
  // RP-21: Platform Role state — seeded from mock data (will come from backend)
  // Using modulo to give each employee a deterministic mock role
  const seedRoleId = React.useMemo(() => {
    if (!employee) return MOCK_ROLES[0].id;
    const idx = Math.abs(employee.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)) % MOCK_ROLES.length;
    return MOCK_ROLES[idx].id;
  }, [employee?.id]);

  const [platformRoleId, setPlatformRoleId] = React.useState<string>(seedRoleId);

  // Reset when the employee changes
  React.useEffect(() => {
    setPlatformRoleId(seedRoleId);
  }, [seedRoleId]);

  // Treat viewer as admin for now — backend will resolve this
  const isAdmin = true;

  const selectedRole = MOCK_ROLES.find((r) => r.id === platformRoleId) ?? MOCK_ROLES[0];

  if (!employee) return null;

  const statusColors = {
    Active: 'bg-success-100 text-success-800 border-success-200',
    'On Leave': 'bg-warning-100 text-warning-800 border-warning-200',
    Inactive: 'bg-neutral-100 text-neutral-800 border-neutral-200',
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl p-0 flex flex-col" aria-describedby={undefined}>
        {/* Header Section */}
        <div className="p-6 border-b">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 rounded-lg">
              <AvatarImage src={employee.image} alt={employee.name} />
              <AvatarFallback className="text-xl">
                {employee.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <SheetTitle className="text-2xl font-bold">{employee.name}</SheetTitle>
                  <p className="text-neutral-600 mt-1">{employee.role}</p>
                </div>
                <Badge
                  className={`${statusColors[employee.status]}`}
                  variant="outline"
                >
                  {employee.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Section */}
        <div className="px-6 py-4 bg-muted border-b">
          <h3 className="text-sm font-semibold text-neutral-700 mb-3">Profile</h3>
          <div className="grid grid-cols-2 gap-6">
            {/* Contact Column */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-neutral-500 uppercase">Contact</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-neutral-400" />
                  <span className="text-teal-600">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-neutral-400" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                {employee.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-neutral-400" />
                    <span>{employee.location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Work Column */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-neutral-500 uppercase">Work</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-600">Type:</span>
                  <Badge variant="secondary" className="ml-auto">
                    {employee.type}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-600">Role:</span>
                  <span className="ml-auto font-medium">{employee.role}</span>
                </div>
                {employee.grade && (
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingUp className="h-4 w-4 text-neutral-400" />
                    <span className="text-neutral-600">Grade:</span>
                    <Badge variant="outline" className="ml-auto">
                      {employee.grade}
                    </Badge>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-600">Department:</span>
                  <span className="ml-auto font-medium">{employee.department}</span>
                </div>

                {/* RP-21: Platform Role field */}
                <div className="flex items-start gap-2 text-sm pt-1">
                  <Shield className="h-4 w-4 text-neutral-400 mt-0.5 shrink-0" />
                  <span className="text-neutral-600 shrink-0">Platform Role:</span>
                  <div className="ml-auto min-w-0">
                    {isAdmin ? (
                      <Select value={platformRoleId} onValueChange={setPlatformRoleId}>
                        <SelectTrigger className="h-7 text-xs w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MOCK_ROLES.map((r) => (
                            <SelectItem key={r.id} value={r.id} className="text-xs">
                              {r.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="font-medium">{selectedRole.name}</span>
                    )}
                  </div>
                </div>

                {/* RP-22: Manage role permissions link */}
                <div className="flex items-center gap-2 text-sm">
                  <ExternalLink className="h-4 w-4 text-neutral-400 shrink-0" />
                  <button
                    type="button"
                    className="text-teal-600 hover:text-teal-700 hover:underline font-medium text-xs"
                    onClick={() => {
                      onOpenChange(false);
                      navigate(`/settings/user-permissions/${platformRoleId}`);
                    }}
                  >
                    Manage role permissions →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="flex-1 overflow-hidden">
          <Tabs defaultValue="skills" className="h-full flex flex-col">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-6">
              <TabsTrigger value="skills" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Skills
              </TabsTrigger>
              <TabsTrigger value="rates" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Rates
              </TabsTrigger>
              <TabsTrigger value="working-pattern" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Working Pattern
              </TabsTrigger>
              <TabsTrigger value="projects" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Projects
              </TabsTrigger>
              <TabsTrigger value="company-access" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent">
                Company Access
              </TabsTrigger>
            </TabsList>

            {/* Skills Tab */}
            <TabsContent value="skills" className="flex-1 overflow-y-auto p-6 mt-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Skills & Proficiency</h3>
              </div>
              <div className="space-y-3">
                {employee.skills.length === 0 && (
                  <div className="text-center py-12">
                    <Star className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
                    <p className="text-neutral-500">No skills recorded</p>
                  </div>
                )}
                {employee.skills.map((skill, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium">{skill.name}</p>
                        {skill.category && (
                          <Badge variant="secondary" className="text-xs">
                            {skill.category}
                          </Badge>
                        )}
                      </div>
                      {skill.proficiency > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <StarRating rating={skill.proficiency} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Rates Tab */}
            <TabsContent value="rates" className="flex-1 overflow-y-auto p-6 mt-0">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Bill Rates</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-100">
                      <p className="text-sm text-neutral-600 mb-1">Day Rate</p>
                      <p className="text-2xl font-bold text-teal-700">
                        £{employee.rates.billRate.day.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-100">
                      <p className="text-sm text-neutral-600 mb-1">Hour Rate</p>
                      <p className="text-2xl font-bold text-teal-700">
                        £{employee.rates.billRate.hour.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-lg font-semibold mb-4">Cost Rates</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted p-4 rounded-lg border border-border">
                      <p className="text-sm text-neutral-600 mb-1">Day Rate</p>
                      <p className="text-2xl font-bold text-neutral-700">
                        £{employee.rates.costRate.day.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-muted p-4 rounded-lg border border-border">
                      <p className="text-sm text-neutral-600 mb-1">Hour Rate</p>
                      <p className="text-2xl font-bold text-neutral-700">
                        £{employee.rates.costRate.hour.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Working Pattern Tab */}
            <TabsContent value="working-pattern" className="flex-1 overflow-y-auto p-6 mt-0">
              <div className="space-y-6">
                {/* Header with Pattern Name and Total Hours */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Working Pattern</h3>
                    <Badge className="bg-teal-600 text-white hover:bg-teal-600">{employee.workingPattern.name}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-neutral-600 mb-1">Total Hours</p>
                    <p className="text-4xl font-bold text-foreground">{employee.workingPattern.totalHoursPerWeek} h</p>
                  </div>
                </div>

                {/* Working Days & Hours Table */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Working Days & Hours</h4>
                  <div className="border rounded-lg overflow-hidden">
                    {/* Table Header */}
                    <div className="grid grid-cols-3 bg-muted border-b">
                      <div className="px-4 py-3 text-sm font-medium text-neutral-700">Day</div>
                      <div className="px-4 py-3 text-sm font-medium text-neutral-700 text-center">Status</div>
                      <div className="px-4 py-3 text-sm font-medium text-neutral-700 text-right">Hours</div>
                    </div>
                    {/* Table Rows */}
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => {
                      const isWorking = employee.workingPattern.workingDays.includes(day);
                      const hours = isWorking
                        ? (employee.workingPattern.dailyHours?.[day] ?? employee.workingPattern.hoursPerDay)
                        : 0;
                      return (
                        <div
                          key={day}
                          className={`grid grid-cols-3 border-b last:border-b-0 ${
                            isWorking ? 'bg-teal-50/30' : 'bg-white'
                          }`}
                        >
                          <div className={`px-4 py-3 text-sm ${isWorking ? 'text-foreground' : 'text-neutral-400'}`}>
                            {day}
                          </div>
                          <div className="px-4 py-3 flex justify-center">
                            <div
                              className={`w-10 h-6 rounded-full transition-colors ${
                                isWorking ? 'bg-teal-500' : 'bg-neutral-300'
                              } relative`}
                            >
                              <div
                                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                                  isWorking ? 'right-0.5' : 'left-0.5'
                                }`}
                              />
                            </div>
                          </div>
                          <div className={`px-4 py-3 text-sm text-right ${isWorking ? 'text-foreground' : 'text-neutral-400'}`}>
                            {hours.toFixed(2)} h
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Summary Section */}
                <div className="bg-teal-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-neutral-600 mb-1">Working Days</p>
                      <p className="text-2xl font-bold text-foreground">{employee.workingPattern.workingDays.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600 mb-1">Total Hours</p>
                      <p className="text-2xl font-bold text-foreground">{employee.workingPattern.totalHoursPerWeek} h</p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600 mb-1">Avg per Day</p>
                      <p className="text-2xl font-bold text-foreground">{employee.workingPattern.hoursPerDay.toFixed(2)} h</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Projects Tab */}
            <TabsContent value="projects" className="flex-1 overflow-y-auto p-6 mt-0">
              <h3 className="text-lg font-semibold mb-4">Project Allocations</h3>
              {employee.projectAllocations.length > 0 ? (
                <div className="space-y-3">
                  {employee.projectAllocations.map((project, index) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <button
                              onClick={() => navigate(`/projects/${project.projectId}`)}
                              className="text-left hover:underline focus:outline-none focus:underline"
                            >
                              <p className="font-semibold text-lg text-teal-600 hover:text-teal-700">
                                {project.projectName}
                              </p>
                              <p className="text-sm text-teal-500 hover:text-teal-600">{project.projectCode}</p>
                            </button>
                          </div>
                          <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                            {project.allocation}% Allocated
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Briefcase className="h-4 w-4 text-neutral-400" />
                            <span className="text-neutral-600">Role:</span>
                            <span className="font-medium">{project.role}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-neutral-600">
                            <Calendar className="h-4 w-4 text-neutral-400" />
                            <span>
                              {new Date(project.startDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}{' '}
                              -{' '}
                              {new Date(project.endDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-500">No current project allocations</p>
                </div>
              )}
            </TabsContent>

            {/* Company Access Tab */}
            <TabsContent value="company-access" className="flex-1 overflow-y-auto p-6 mt-0">
              <ResourceCompanyAccessTab employeeId={employee.id} />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ---------------------------------------------------------------------------
// ResourceCompanyAccessTab
// Displays all companies and lets the user toggle access + set a default.
// ---------------------------------------------------------------------------

const ResourceCompanyAccessTab: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const [rows, setRows] = useState<EmployeeCompanyAccessRow[]>([]);
  const [pending, setPending] = useState<Map<number, { hasAccess: boolean; isDefault: boolean }>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Keep track of which employee we last loaded so we reload when the sheet reopens for a different resource
  const loadedFor = useRef<string | null>(null);

  const loadAccess = useCallback(async (id: string) => {
    setIsLoading(true);
    setPending(new Map());
    try {
      const data = await getEmployeeCompanyAccess(id);
      setRows(Array.isArray(data) ? data : []);
      loadedFor.current = id;
    } catch (err) {
      console.error('Failed to load company access for employee', id, err);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (employeeId !== loadedFor.current) {
      loadAccess(employeeId);
    }
  }, [employeeId, loadAccess]);

  // Merge persisted rows with any unsaved pending changes
  const displayRows = useMemo(() => {
    return rows.map((row) => {
      const p = pending.get(row.companyId);
      return p ? { ...row, ...p } : row;
    });
  }, [rows, pending]);

  function setPendingRow(companyId: number, updates: Partial<{ hasAccess: boolean; isDefault: boolean }>) {
    setPending((prev) => {
      const next = new Map(prev);
      const existing = next.get(companyId) ?? { hasAccess: rows.find(r => r.companyId === companyId)?.hasAccess ?? false, isDefault: rows.find(r => r.companyId === companyId)?.isDefault ?? false };
      next.set(companyId, { ...existing, ...updates });
      return next;
    });
  }

  function handleToggleAccess(companyId: number, value: boolean) {
    setPendingRow(companyId, { hasAccess: value, isDefault: value ? (pending.get(companyId)?.isDefault ?? rows.find(r => r.companyId === companyId)?.isDefault ?? false) : false });
  }

  function handleSetDefault(companyId: number) {
    // Only one company can be default; when setting a new default, clear all others
    setPending((prev) => {
      const next = new Map<number, { hasAccess: boolean; isDefault: boolean }>();
      rows.forEach((row) => {
        const existing = prev.get(row.companyId) ?? { hasAccess: row.hasAccess, isDefault: row.isDefault };
        if (row.companyId === companyId) {
          next.set(row.companyId, { ...existing, hasAccess: true, isDefault: true });
        } else {
          next.set(row.companyId, { ...existing, isDefault: false });
        }
      });
      return next;
    });
  }

  const isDirty = pending.size > 0;

  // Count assigned companies from the merged display rows
  const assignedCount = displayRows.filter((r) => r.hasAccess).length;
  const defaultCount = displayRows.filter((r) => r.isDefault).length;

  async function handleSave() {
    if (!isDirty) return;
    setIsSaving(true);
    try {
      // Send the full list of all companies (merged with pending changes)
      // because the backend replaces all access entries for the employee.
      const updates = displayRows.map((row) => ({
        companyId: row.companyId,
        hasAccess: row.hasAccess,
        isDefault: row.isDefault,
      }));
      const updated = await updateEmployeeCompanyAccess(employeeId, updates);
      setRows(updated);
      setPending(new Map());
      toast.success('Company access saved');
    } catch {
      toast.error('Failed to save company access');
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Company Access</h3>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center justify-between py-3 border-b">
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-md" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <Skeleton className="h-6 w-11 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Company Access</h3>

      <div className="space-y-0">
        {displayRows.map((row) => (
          <div
            key={row.companyId}
            className="flex items-center justify-between py-3 border-b last:border-b-0"
          >
            {/* Company info */}
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-neutral-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-4 w-4 text-neutral-500" />
              </div>
              <div>
                <p className={`text-sm font-medium leading-tight ${!row.hasAccess ? 'text-neutral-400' : 'text-foreground'}`}>
                  {row.companyName}
                </p>
                <p className={`text-xs leading-tight ${!row.hasAccess ? 'text-neutral-300' : 'text-neutral-500'}`}>
                  {row.companyCode}
                </p>
              </div>
            </div>

            {/* Right side: "Set as default" + toggle */}
            <div className="flex items-center gap-3">
              {row.hasAccess && (
                <button
                  onClick={() => handleSetDefault(row.companyId)}
                  className={`text-xs transition-colors ${
                    row.isDefault
                      ? 'text-teal-600 font-medium cursor-default'
                      : 'text-neutral-400 hover:text-teal-600 underline underline-offset-2'
                  }`}
                  disabled={row.isDefault}
                >
                  {row.isDefault ? 'Default' : 'Set as default'}
                </button>
              )}
              <Switch
                checked={row.hasAccess}
                onCheckedChange={(val) => handleToggleAccess(row.companyId, val)}
                className="data-[state=checked]:bg-teal-600"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Summary line */}
      <p className="text-xs text-neutral-500">
        {assignedCount === 0
          ? 'No companies assigned.'
          : `${assignedCount} ${assignedCount === 1 ? 'company' : 'companies'} assigned${defaultCount === 0 ? ' — no default set' : ''}.`}
      </p>

      {/* Save button */}
      <Button
        onClick={handleSave}
        disabled={!isDirty || isSaving}
        className="w-full bg-teal-600 hover:bg-teal-700 text-white"
      >
        {isSaving ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Save className="h-4 w-4 mr-2" />
        )}
        Save Changes
      </Button>
    </div>
  );
};

// Default column definitions for Resource Directory
const DEFAULT_DIRECTORY_COLUMNS: ColumnDefinition[] = [
  {
    id: 'name',
    name: 'Name',
    description: 'Employee name with avatar',
    category: 'Status',
    visible: true,
    locked: true,
    order: 0,
  },
  {
    id: 'role',
    name: 'Role',
    description: 'Job title or role',
    category: 'Status',
    visible: true,
    order: 1,
  },
  {
    id: 'department',
    name: 'Department',
    description: 'Department or team',
    category: 'Status',
    visible: true,
    order: 2,
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Email address',
    category: 'Client',
    visible: true,
    order: 3,
  },
  {
    id: 'phone',
    name: 'Phone',
    description: 'Phone number',
    category: 'Client',
    visible: true,
    order: 4,
  },
  {
    id: 'location',
    name: 'Location',
    description: 'Office location',
    category: 'Status',
    visible: true,
    order: 5,
  },
  {
    id: 'type',
    name: 'Type',
    description: 'Person or Asset',
    category: 'Status',
    visible: true,
    order: 6,
  },
  {
    id: 'status',
    name: 'Status',
    description: 'Employment status',
    category: 'Status',
    visible: false,
    order: 7,
  },
  {
    id: 'grade',
    name: 'Grade',
    description: 'Seniority level',
    category: 'Resource',
    visible: false,
    order: 8,
  },
  {
    id: 'resource-type',
    name: 'Resource Type',
    description: 'Technical, Creative, etc.',
    category: 'Resource',
    visible: false,
    order: 9,
  },
  {
    id: 'fte',
    name: 'FTE',
    description: 'Full-time equivalent',
    category: 'Resource',
    visible: false,
    order: 10,
    align: 'right',
  },
  {
    id: 'hours-per-week',
    name: 'Hours/Week',
    description: 'Total hours per week',
    category: 'Resource',
    visible: false,
    order: 11,
    align: 'right',
  },
  {
    id: 'bill-rate-day',
    name: 'Bill Rate (Day)',
    description: 'Daily billing rate',
    category: 'Financial',
    visible: false,
    order: 12,
    align: 'right',
  },
  {
    id: 'bill-rate-hour',
    name: 'Bill Rate (Hour)',
    description: 'Hourly billing rate',
    category: 'Financial',
    visible: false,
    order: 13,
    align: 'right',
  },
  {
    id: 'cost-rate-day',
    name: 'Cost Rate (Day)',
    description: 'Daily cost rate',
    category: 'Financial',
    visible: false,
    order: 14,
    align: 'right',
  },
  {
    id: 'cost-rate-hour',
    name: 'Cost Rate (Hour)',
    description: 'Hourly cost rate',
    category: 'Financial',
    visible: false,
    order: 15,
    align: 'right',
  },
  {
    id: 'total-allocation',
    name: 'Total Allocation',
    description: 'Total project allocation %',
    category: 'Performance',
    visible: false,
    order: 16,
    align: 'right',
  },
  {
    id: 'project-count',
    name: 'Active Projects',
    description: 'Number of active projects',
    category: 'Performance',
    visible: false,
    order: 17,
    align: 'right',
  },
];

export function DirectoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedEmployee, setSelectedEmployee] = useState<DirectoryResource | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [directoryFilters, setDirectoryFilters] = useState<DirectoryFilters>(() => {
    // Initialise skill filter from URL ?skill= param (coming from Skills page)
    const skillFromUrl = searchParams.get('skill');
    if (skillFromUrl) {
      return { ...EMPTY_DIRECTORY_FILTERS, skills: [skillFromUrl] };
    }
    return EMPTY_DIRECTORY_FILTERS;
  });
  const [employees, setEmployees] = useState<DirectoryResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [apiEmployees, allocations, skillsSummary] = await Promise.all([
        fetchEmployees(),
        fetchResourceAllocations().catch(() => [] as ResourceAllocation[]),
        fetchSkills().catch(() => [] as SkillSummaryResponse[]),
      ]);
      const skillsMap = buildEmployeeSkillsMap(skillsSummary);
      const mapped = (apiEmployees as ApiEmployee[]).map(emp =>
        mapApiEmployee(emp, allocations as ResourceAllocation[], skillsMap)
      );
      setEmployees(mapped);
    } catch (err) {
      console.error('Failed to load directory data:', err);
      setError('Failed to load employee data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Use column preferences hook for persistence
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: 'directory-column-preferences',
    defaultColumns: DEFAULT_DIRECTORY_COLUMNS,
  });

  // Pagination (don't persist to URL to keep URLs clean)
  const {
    currentPage,
    pageSize,
    setPage,
    setPageSize,
    resetPagination,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false });

  // Build available options for the filter sheet
  const filterAvailableOptions = useMemo(() => {
    const skillNames = [...new Set(
      employees.flatMap((e) => e.skills.map((s) => s.name)).filter(Boolean)
    )].sort();
    const deptNames = [...new Set(employees.map((e) => e.department).filter(Boolean))].sort();
    const roleNames = [...new Set(employees.map((e) => e.role).filter(Boolean))].sort();
    const typeNames = [...new Set(employees.map((e) => e.type).filter(Boolean))].sort();
    const statusNames = [...new Set(employees.map((e) => e.status).filter(Boolean))].sort();
    return {
      skills: skillNames.map((s) => ({ value: s, label: s })),
      departments: deptNames.map((d) => ({ value: d, label: d })),
      roles: roleNames.map((r) => ({ value: r, label: r })),
      types: typeNames.map((t) => ({ value: t, label: t })),
      statuses: statusNames.map((s) => ({ value: s, label: s })),
    };
  }, [employees]);

  // Clean the ?skill= URL param once the filter has been applied (don't auto-open the filter sheet)
  const hasCleanedUrl = useRef(false);
  useEffect(() => {
    if (!hasCleanedUrl.current && searchParams.get('skill') && hasActiveDirectoryFilters(directoryFilters) && !loading) {
      hasCleanedUrl.current = true;
      searchParams.delete('skill');
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, directoryFilters, searchParams, setSearchParams]);

  // Active filter count for the badge
  const activeFilterCount = useMemo(
    () => countActiveDirectoryFilters(directoryFilters),
    [directoryFilters]
  );

  // Filter and sort employees
  const filteredEmployees = useMemo(() => {
    return employees
      .filter((employee) => {
        const matchesSearch =
          employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          employee.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
          employee.email.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesSkills =
          directoryFilters.skills.length === 0 ||
          directoryFilters.skills.some((skill) =>
            employee.skills.some((s) => s.name === skill)
          );
        const matchesDepartment =
          directoryFilters.departments.length === 0 ||
          directoryFilters.departments.includes(employee.department);
        const matchesRole =
          directoryFilters.roles.length === 0 ||
          directoryFilters.roles.includes(employee.role);
        const matchesType =
          directoryFilters.types.length === 0 ||
          directoryFilters.types.includes(employee.type);
        const matchesStatus =
          directoryFilters.statuses.length === 0 ||
          directoryFilters.statuses.includes(employee.status);
        return matchesSearch && matchesSkills && matchesDepartment && matchesRole && matchesType && matchesStatus;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, searchQuery, directoryFilters]);

  // Reset to first page when filters change
  useEffect(() => {
    resetPagination();
  }, [searchQuery, directoryFilters]);

  // Paginate the filtered results
  const paginatedResult = useMemo(() => {
    return paginateArray(filteredEmployees, currentPage, pageSize);
  }, [filteredEmployees, currentPage, pageSize]);

  const displayedEmployees = paginatedResult.data;
  const pagination = paginatedResult.pagination;

  const handleEmployeeClick = (employee: DirectoryResource) => {
    setSelectedEmployee(employee);
    setSheetOpen(true);
  };

  // Get visible columns sorted by order
  const visibleColumns = useMemo(() => {
    return columns
      .filter((col) => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);

  // Helper function to render table cell content based on column ID
  const renderTableCell = (employee: DirectoryResource, columnId: string) => {
    switch (columnId) {
      case 'name':
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 rounded-md">
              <AvatarImage src={employee.image} alt={employee.name} />
              <AvatarFallback>
                {employee.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{employee.name}</span>
          </div>
        );
      case 'role':
        return employee.role;
      case 'department':
        return <Badge variant="outline">{employee.department}</Badge>;
      case 'email':
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span>{employee.email}</span>
          </div>
        );
      case 'phone':
        return employee.phone ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span>{employee.phone}</span>
          </div>
        ) : <span className="text-muted-foreground">-</span>;
      case 'location':
        return employee.location ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>{employee.location}</span>
          </div>
        ) : <span className="text-muted-foreground">-</span>;
      case 'type':
        return <Badge variant="secondary">{employee.type}</Badge>;
      case 'status':
        return <Badge variant={employee.status === 'Active' ? 'default' : 'secondary'}>{employee.status}</Badge>;
      case 'grade':
        return employee.grade || <span className="text-muted-foreground">-</span>;
      case 'resource-type':
        return employee.resourceType || <span className="text-muted-foreground">-</span>;
      case 'fte':
        return employee.workingPattern.fte.toFixed(2);
      case 'hours-per-week':
        return employee.workingPattern.totalHoursPerWeek;
      case 'bill-rate-day':
        return `${employee.rates.billRate.day.toLocaleString()}`;
      case 'bill-rate-hour':
        return `${employee.rates.billRate.hour.toLocaleString()}`;
      case 'cost-rate-day':
        return `${employee.rates.costRate.day.toLocaleString()}`;
      case 'cost-rate-hour':
        return `${employee.rates.costRate.hour.toLocaleString()}`;
      case 'total-allocation':
        const totalAllocation = employee.projectAllocations.reduce((sum, alloc) => sum + alloc.allocation, 0);
        return `${totalAllocation}%`;
      case 'project-count':
        return employee.projectAllocations.length;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Resource Directory</h1>
        <p className="text-neutral-600 mt-1">Browse and manage your organization's resources</p>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4" data-tour="directory-search">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, role, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchQuery('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {hasActiveDirectoryFilters(directoryFilters) && (
          <Button
            variant="outline"
            onClick={() => setDirectoryFilters(EMPTY_DIRECTORY_FILTERS)}
            className="text-muted-foreground"
          >
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => setFilterSheetOpen(true)}
          className="h-9 w-9 p-0 relative"
          aria-label="Filter"
        >
          <Filter className="h-4 w-4" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 text-[10px] font-semibold text-primary-foreground min-w-[18px] h-[18px] rounded-full bg-primary inline-flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>

        <div className="flex gap-1 border rounded-md" data-tour="directory-view-toggle">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="rounded-r-none"
          >
            <Grid3x3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="rounded-l-none"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>

        {viewMode === 'list' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setColumnConfigOpen(true)}
            className="gap-2"
          >
            <Settings2 className="h-4 w-4" />
            Columns
          </Button>
        )}
      </div>

      {/* Results count */}
      <div className="text-sm text-neutral-600">
        Showing <span className="font-semibold">{pagination.startIndex + 1}</span> to{' '}
        <span className="font-semibold">{pagination.endIndex}</span> of{' '}
        <span className="font-semibold">{filteredEmployees.length}</span> employees
        {filteredEmployees.length !== employees.length && (
          <span className="text-neutral-500"> (filtered from {employees.length} total)</span>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-neutral-500">Loading employees...</p>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="text-center py-12">
          <p className="text-error-500 mb-2">{error}</p>
          <Button variant="outline" onClick={loadEmployees}>Retry</Button>
        </div>
      )}

      {/* Employee Grid/List */}
      {!loading && !error && displayedEmployees.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6" data-tour="directory-employee-grid">
            {displayedEmployees.map((employee) => (
              <Card
                key={employee.id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handleEmployeeClick(employee)}
              >
                <div className="relative overflow-hidden h-48">
                  {employee.image ? (
                    <img
                      src={employee.image}
                      alt={employee.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        // Hide the image and show fallback by replacing the parent content
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          e.currentTarget.style.display = 'none';
                          const fallback = document.createElement('div');
                          fallback.className = 'w-full h-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center group-hover:scale-105 transition-transform duration-300';
                          const initials = document.createElement('span');
                          initials.className = 'text-5xl font-semibold text-white';
                          initials.textContent = employee.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('');
                          fallback.appendChild(initials);
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                      <span className="text-5xl font-semibold text-white">
                        {employee.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </span>
                    </div>
                  )}
                  <Badge
                    className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm"
                    variant="secondary"
                  >
                    {employee.type}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-1">{employee.name}</h3>
                  <p className="text-sm text-neutral-600 mb-3">{employee.role}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-neutral-600">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{employee.email}</span>
                    </div>
                    {employee.phone && (
                      <div className="flex items-center gap-2 text-neutral-600">
                        <Phone className="h-3 w-3" />
                        <span>{employee.phone}</span>
                      </div>
                    )}
                    {employee.location && (
                      <div className="flex items-center gap-2 text-neutral-600">
                        <MapPin className="h-3 w-3" />
                        <span>{employee.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <Badge variant="outline" className="text-xs">
                      {employee.department}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      className={column.align === 'right' ? 'text-right' : ''}
                    >
                      {column.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedEmployees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleEmployeeClick(employee)}
                  >
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={column.align === 'right' ? 'text-right' : ''}
                      >
                        {renderTableCell(employee, column.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : !loading && !error ? (
        <div className="text-center py-12">
          <p className="text-neutral-500">No employees found matching your criteria.</p>
          <p className="text-sm text-neutral-400 mt-2">Try adjusting your search or filters.</p>
        </div>
      ) : null}

      {/* Pagination Controls */}
      {filteredEmployees.length > 0 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          showPageSizeSelector={true}
        />
      )}

      {/* Resource Detail Sheet */}
      <ResourceDetailSheet
        employee={selectedEmployee}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        navigate={navigate}
      />

      {/* Column Configurator Drawer */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={updateColumns}
        defaultColumns={DEFAULT_DIRECTORY_COLUMNS}
        pageKey="directory"
      />

      {/* Directory Filter Sheet */}
      <DirectoryFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={directoryFilters}
        onApply={setDirectoryFilters}
        onClear={() => setDirectoryFilters(EMPTY_DIRECTORY_FILTERS)}
        availableOptions={filterAvailableOptions}
      />
    </div>
  );
}
